import {Text, Icon, Button, TextInput} from '@/ui-kits';
import {useState} from 'react';
import {X} from 'lucide-react';
import { cn } from '@/utils/common_functions';

interface ArrayInputClinicalNoteProps {
  label: string;
  index: number;
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  addButtonText?: string;
  isEditing: boolean;
  onToggleEdit: () => void;
  isReadonly: boolean;
}

export const ArrayInputClinicalNote = (props: ArrayInputClinicalNoteProps) => {
  const {label, index, values, onChange, addButtonText = 'ADD', placeholder, isEditing, onToggleEdit, isReadonly} = props;
  const [value, setValue] = useState<string>('');

  const handleAdd = (newValue: string) => {
    if (newValue.trim() === '') {
      return;
    }
    onChange([...values, newValue]);
    setValue('');
  };

  const handleRemove = (indexToRemove: number) => {
    const newValues = values.filter((_, i) => i !== indexToRemove);
    onChange(newValues);
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Label with edit icon */}
      <div className="flex items-center gap-2 mb-2 sm:mb-3">
        <div className="size-5 sm:size-6 bg-gradient-to-br from-ui_blue to-ui_blue_light text-white rounded-full flex items-center justify-center">
          <Text variant="small" className="!text-xs sm:!text-sm">{index}</Text>
        </div>
        <Text variant="subtitle" className="text-ui_blue !text-base sm:!text-lg">
          {label}
        </Text>
      </div>

      {/* values list */}
      <div className={cn("border flex flex-wrap px-2 sm:px-3 py-2 gap-1.5 sm:gap-2 border-ui_input_border bg-white relative !text-sm sm:!text-base rounded-lg overflow-hidden items-center disabled:cursor-not-allowed", isEditing && 'border-ui_blue')}>
        {values.length === 0 ? (
          <Text variant="body" className="text-ui_grey !text-xs sm:!text-sm">
            No {label} added
          </Text>
        ) : (
          values.map((value, index) => (
            <span key={`${label}-${index}`} className="inline-flex items-center gap-1.5 sm:gap-2 bg-blue-50 rounded-full px-2 sm:px-3 py-0.5 sm:py-1">
              <Text variant="body" className="text-ui_blue font-FigtreeMedium !text-xs sm:!text-sm">
                {value}
              </Text>
              {isEditing && <X className="size-4 sm:size-5 text-ui_input_border_dark cursor-pointer" onClick={() => handleRemove(index)} />}
            </span>
          ))
        )}
        {!isEditing && !isReadonly && (
          <button onClick={onToggleEdit} className="self-start ml-auto cursor-pointer">
            <Icon type="edit_input" className="size-3.5 sm:size-4" />
          </button>
        )}
      </div>

      {/* add new value input */}
      {isEditing && !isReadonly && (
        <div className="flex flex-col sm:flex-row gap-2">
          <TextInput
            className="bg-white w-full sm:max-w-[300px]"
            wrapperClassName="border" // make border 1 px
            value={value}
            onChangeText={setValue}
            placeholder={placeholder}
          />
          <Button variant="secondary" className="bg-white w-full sm:w-auto" title="Add" onClick={() => handleAdd(value)}></Button>
        </div>
      )}
    </div>
  );
};
